/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import { collection, onSnapshot, query, where, addDoc, deleteDoc, doc, getDocs } from 'firebase/firestore'
import { db } from '../../firebase'
import { Box, Button, Chip, Typography, useTheme, Grid, Backdrop, CircularProgress } from "@mui/material"
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DataGrid, gridNumberComparator } from "@mui/x-data-grid"
import { tokens } from "../../theme"
import MinceProductForm from "./lib/MinceProductForm"
import ShippingDestinationForm from "./lib/ShippingDestinationForm"
import { printMinceProductLabel } from "./lib/PrintLabel"
import dayjs from "dayjs"
import 'dayjs/locale/ja'

const getInitialDateRange = () => {
  const today = dayjs()
  const currentYear = today.year()
  const currentMonth = today.month()
  if (currentMonth < 3) {
    return {
      startDate: dayjs().year(currentYear - 1).month(3).date(1),
      endDate: dayjs().year(currentYear).month(2).date(31)
    }
  }
  return {
    startDate: dayjs().year(currentYear).month(3).date(1),
    endDate: dayjs().year(currentYear + 1).month(2).date(31)
  }
}

const MinceProductList = () => {
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const [items, setItems] = useState<Array<Record<string, any>>>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [open, setOpen] = useState<boolean>(false)
  const [openShipping, setOpenShipping] = useState<boolean>(false)
  const [selectedProductId, setSelectedProductId] = useState<string>('')
  const productsRef = collection(db, 'products')

  const [dateRange, setDateRange] = useState(() => {
    const saved = sessionStorage.getItem('minceProductListDateRange')
    if (saved) {
      const parsed = JSON.parse(saved)
      return {
        startDate: parsed.startDate ? dayjs(parsed.startDate) : null,
        endDate: parsed.endDate ? dayjs(parsed.endDate) : null
      }
    }
    return getInitialDateRange()
  })

  const handleStartDateChange = (newDate: dayjs.Dayjs | null) => {
    const next = { ...dateRange, startDate: newDate }
    setDateRange(next)
    sessionStorage.setItem('minceProductListDateRange', JSON.stringify({
      startDate: newDate?.toISOString(),
      endDate: dateRange.endDate?.toISOString()
    }))
  }

  const handleEndDateChange = (newDate: dayjs.Dayjs | null) => {
    const next = { ...dateRange, endDate: newDate }
    setDateRange(next)
    sessionStorage.setItem('minceProductListDateRange', JSON.stringify({
      startDate: dateRange.startDate?.toISOString(),
      endDate: newDate?.toISOString()
    }))
  }

  const totalWeight = items.reduce((sum, item) => sum + (parseFloat(item.weight) || 0), 0)

  const columns = [
    {
      field: "productNo",
      headerName: "製品番号",
      width: 180,
      sortComparator: gridNumberComparator,
    },
    {
      field: "processingDate",
      headerName: "加工日",
      flex: 1,
    },
    {
      field: "type",
      headerName: "獣種",
      flex: 1,
    },
    {
      field: "weight",
      headerName: "重量(g)",
      flex: 1,
    },
    {
      field: "storageType",
      headerName: "保存方法",
      flex: 1,
    },
    {
      field: "expirationDate",
      headerName: "消費期限",
      flex: 1,
    },
    {
      field: "barcode",
      headerName: "ラベル",
      width: 100,
      renderCell: (params: any) => (
        <Chip
          label="ラベル印刷"
          color="warning"
          size="small"
          onClick={() => printMinceProductLabel({
            name: `${params.row.type}肉`,
            weight: params.row.weight,
            expirationDate: params.row.expirationDate,
            productNo: params.row.productNo
          })}
        />
      ),
    },
    {
      field: "destination",
      headerName: "出荷先",
      flex: 1,
      renderCell: (params: any) => (
        <Chip
          label={params.row.customer || "ー"}
          color={params.row.customer ? "info" : "default"}
          size="small"
          onClick={() => openShippingDetail(params.row.id)}
        />
      ),
    },
    {
      field: "actions",
      headerName: "操作",
      width: 180,
      renderCell: (params: any) => (
        <Box display="flex" gap={1}>
          <Chip label="編集" color="default" size="small" onClick={() => openDetail(params.row.id)} />
          <Chip label="複製" color="secondary" size="small" onClick={() => copyProduct(params.row.id)} />
          <Chip label="削除" color="error" size="small" onClick={() => deleteProduct(params.row.id)} />
        </Box>
      ),
    }
  ]

  useEffect(() => {
    setLoading(true)
    const timeoutId = setTimeout(() => setLoading(false), 10000)

    // orderBy を使わず equality filter のみにすることで複合インデックス不要
    const q = query(productsRef, where('isMinced', '==', true))

    const unsubscribe = onSnapshot(q, ({ docs }) => {
      const start = dateRange.startDate?.format('YYYY/MM/DD') ?? null
      const end = dateRange.endDate?.add(1, 'day').format('YYYY/MM/DD') ?? null

      const filtered = docs
        .map(d => ({ ...d.data(), id: d.id }) as Record<string, any>)
        .filter(d => {
          const date = d.processingDate as string
          if (start && date < start) return false
          if (end && date >= end) return false
          return true
        })
        .sort((a, b) => {
          const da = a.processingDate as string
          const db_ = b.processingDate as string
          return da < db_ ? 1 : da > db_ ? -1 : 0
        })
        .map((d, i) => ({ No: i + 1, ...d }))

      setItems(filtered)
      setLoading(false)
    }, (error) => {
      console.error('MinceProductList query error:', error)
      setLoading(false)
    })

    return () => {
      unsubscribe()
      clearTimeout(timeoutId)
    }
  }, [dateRange])

  const openDetail = (productId: string) => {
    setSelectedProductId(productId)
    setOpen(true)
  }

  const closeDetail = () => {
    setSelectedProductId("")
    setOpen(false)
  }

  const openShippingDetail = (productId: string) => {
    setSelectedProductId(productId)
    setOpenShipping(true)
  }

  const closeShippingDetail = () => {
    setSelectedProductId("")
    setOpenShipping(false)
  }

  const copyProduct = async (productId: string) => {
    if (!confirm("製品情報を複製しますか？")) return
    const productToCopy = items.find(item => item.id === productId)
    if (!productToCopy) return

    const processingDate = dayjs(productToCopy.processingDate)
    const dateStr = processingDate.format("YYYYMMDD")
    const prefix = `MINC-${dateStr}-`
    const snap = await getDocs(query(productsRef, where('isMinced', '==', true)))
    const maxSeq = snap.docs.reduce((max, d) => {
      const no: string = d.data().productNo || ''
      if (!no.startsWith(prefix)) return max
      const seq = parseInt(no.substring(prefix.length))
      return Math.max(max, isNaN(seq) ? 0 : seq)
    }, 0)
    const productNo = `${prefix}${('000' + (maxSeq + 1)).slice(-3)}`

    const { id, No, ...rest } = productToCopy // eslint-disable-line @typescript-eslint/no-unused-vars
    await addDoc(productsRef, { ...rest, productNo, createdAt: dayjs().format('YYYY/MM/DD hh:mm') })
  }

  const deleteProduct = async (productId: string) => {
    if (!confirm("製品情報を削除しますか？")) return
    await deleteDoc(doc(db, 'products', productId))
  }

  return (
    <Box m={5}>
      <Typography variant="h3" mb={2}>ミンチ製品一覧</Typography>
      <Grid container spacing={2} alignItems="center" mb={2}>
        <Grid item xs={12} md="auto">
          <Button variant="contained" color="info" onClick={() => { setSelectedProductId(''); setOpen(true) }}>
            ミンチ製品追加
          </Button>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>期間</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
            <Box display="flex" alignItems="center" gap={1}>
              <DatePicker
                label="開始日"
                value={dateRange.startDate}
                onChange={handleStartDateChange}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              <Typography>～</Typography>
              <DatePicker
                label="終了日"
                value={dateRange.endDate}
                onChange={handleEndDateChange}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Box>
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md="auto" sx={{ ml: { md: 'auto' } }}>
          <Typography variant="h5" sx={{ color: colors.grey[100] }}>
            合計重量: {totalWeight.toLocaleString()}g ({(totalWeight / 1000).toFixed(2)}kg)
          </Typography>
        </Grid>
      </Grid>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box
        height="80vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        <DataGrid
          rows={items}
          columns={columns}
          loading={loading}
          initialState={{
            sorting: {
              sortModel: [{ field: 'productNo', sort: 'desc' }],
            }
          }}
        />
      </Box>
      <MinceProductForm
        productId={selectedProductId || undefined}
        isOpen={open}
        onClose={closeDetail}
      />
      <ShippingDestinationForm
        productId={selectedProductId}
        isOpen={openShipping}
        onClose={closeShippingDetail}
      />
    </Box>
  )
}

export default MinceProductList
